import { OrderStatus } from '@daymade/common-lib/dbEnums';
import { getAdyenPaymentSessionData } from 'api/payments';
import PlainLoadingSpinner from 'components/PlainLoadingSpinner';
import { EntryMethod } from 'components/enter/EntryMethod';
import MainLayout from 'components/layout/MainLayout';
import AdyenDropInCheckout from 'components/payments/AdyenDropInCheckout';
import { OrderType } from 'components/payments/PayPalAdyenCheckout';
import { DaymadeDrawTypePlanSubcode } from 'constants/Draw';
import { usePayment3ds } from 'hooks/payment-3ds/usePayment3ds';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Router from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { Message } from 'semantic-ui-react';
import { TOrderDetails } from 'types/OrderDetails';
import HttpStatusCode from 'utils/HttpStatusCode';
import {
  ENTER_FLOW_SPLIT_TEST_TRACKING,
  FunnelFlags,
  SplitTest,
  trackFunnelEventIfHaveNotAlready,
} from 'utils/SplitTesting';
import {
  trackEecCheckoutOption,
  trackPurchase,
} from 'utils/analytics/analytics';
import { adyenApiClient, orderApiClient } from 'utils/apiClient';
import { scrollToSection, sleep } from 'utils/common';
import { getCookie, removeCookie } from 'utils/cookie';
import { navigateToConfirmation } from 'utils/navigation';

export const SOURCE_QUERY_PARAM = {
  STANDARD_TICKET_ORDER: 'enter',
  UPGRADE: 'upgrade',
  GIFT_CARDS: 'gift-cards',
  UPDATE_PAYMENT_METHOD: 'change-payment-method',
  TOKENS: 'purchase-tokens',
};

const Validate3DSPayment = observer(() => {
  const {
    adyenPaymentSessionId,
    subscriptionId,
    orderType,
    sourceQueryParam,
    sessionId,
    redirectResult,
    customRedirect,
  } = usePayment3ds();
  const { displayToast, displayHeaderBanner } = useUIStore();
  const { currentUser, updateCurrentUser } = useUserStore();

  const [error, setError] = useState<string | null>(null);

  // This variable is populated by the Adyen payment session's sessionData object which can be used to re-display the payment form to take payment again if the initial attempt failed.
  const [paymentSessionData, setPaymentSessionData] = useState(null);

  const [loading, setLoading] = useState(true);

  // Holds a reference to the timer that periodically checks for order completion.
  // Needed so we can clear it when the component unmounts.
  const orderCompletionTimerRef = useRef<any>(null);

  const [failedToFetchOrder, setFailedToFetchOrder] = useState(false);

  // This function will be periodically called once payment is initiated (if this is a TICKET_ORDER, TOKEN_ORDER, or GIFT_CARD_ORDER).
  const checkRegularOrderStatusAndAdvanceToConfirmation = async () => {
    const orderDetails =
      await orderApiClient.auth.getOrderDetailsByAdyenPaymentSession({
        params: {
          adyen_payment_session_id: adyenPaymentSessionId!,
        },
      });

    if (orderDetails.status !== HttpStatusCode.OK) {
      console.error('Failed to fetch order details');
      return;
    }

    const orderState = orderDetails.body?.order?.status;

    if (orderState === OrderStatus.COMPLETED) {
      console.log('Order is complete, redirecting to confirmation');
      await advanceToConfirmationPage(orderDetails.body);
    } else {
      return Router.reload();
      // const couldNotFetchOrderThisTime =
      //   !orderDetails?.adyenPaymentSession?.sessionData;
      // if (couldNotFetchOrderThisTime) {
      //   if (failedToFetchOrder) {
      //     // Failed twice in a row, so try reloading page?
      //     return Router.reload();
      //   }
      //   setFailedToFetchOrder(true);
      // }
      // orderCompletionTimerRef.current = setTimeout(
      //   checkRegularOrderStatusAndAdvanceToConfirmation,
      //   couldNotFetchOrderThisTime ? 5000 : 2000
      // );
      // // Populate session data, so we can show the payment form again
      // if (!couldNotFetchOrderThisTime) {
      //   setPaymentSessionData(orderDetails.adyenPaymentSession.sessionData);
      // }
    }
  };

  const handlePaymentFromChangePaymentMethodPage = async () => {
    if (!adyenPaymentSessionId) {
      console.error('ERROR: No adyenPaymentSessionId');
      return;
    }

    const result = await adyenApiClient.auth.getAdyenPaymentStatus({
      query: {
        adyenPaymentSessionId: adyenPaymentSessionId,
      },
    });

    if (
      result.status !== HttpStatusCode.OK ||
      result.body.status != 'Authorised'
    ) {
      orderCompletionTimerRef.current = setTimeout(
        handlePaymentFromChangePaymentMethodPage,
        2000
      );
    } else {
      displayToast({
        title: 'Your payment method has been updated.',
        timeout: 5000,
      });
      Router.push('/account/subscriptions');
    }
  };

  const advanceToConfirmationPage = async (orderDetails: TOrderDetails) => {
    removeCookie('referral_id');

    switch (orderType) {
      case OrderType.GIFT_CARD_ORDER:
        return await Router.replace(
          `/gift-cards/confirmation?orderId=${orderDetails.order.id}`
        );

      case OrderType.TOKEN_ORDER:
        if (customRedirect) {
          return await Router.replace(customRedirect);
        }
        return await Router.replace('/token-town');

      case OrderType.TICKET_ORDER: {
        await trackPurchase(
          orderDetails.order.totalFiatAmount.toString(),
          orderDetails.order.id,
          orderDetails.order,
          orderDetails.drawOrder,
          orderDetails.drawSchedulePlans,
          currentUser?.emailAddress,
          orderDetails.promoCode,
          currentUser?.id,
          {
            drawDays: orderDetails.drawSchedulePlans
              ?.map((plan) => plan.drawSchedule.code)
              .join(','),
            numEntries: orderDetails.drawOrder?.entries.length,
            entryMethod: orderDetails.drawSchedulePlans?.[0].drawTypePlan.code,
            boosted: orderDetails.drawSchedulePlans?.some(
              (plan) =>
                plan.drawTypePlan.subcode === DaymadeDrawTypePlanSubcode.BOOST
            ),
            billings: orderDetails.order.totalFiatAmount,
            cost: orderDetails.drawOrder?.price,
          }
        );

        const entryMethod =
          orderDetails.order.isSubscription === false
            ? EntryMethod.OneOff
            : orderDetails.drawSchedulePlans?.[0].drawTypePlan.code === 'LITE'
              ? EntryMethod.LiteSubscription
              : EntryMethod.PremiumSubscription;
        const shouldTrackPurchaseFunnelEvent =
          orderType === OrderType.TICKET_ORDER &&
          sourceQueryParam === SOURCE_QUERY_PARAM.STANDARD_TICKET_ORDER; // Exclude people coming through subscription upgrade flow
        if (shouldTrackPurchaseFunnelEvent) {
          const ongoingFunnelTrackingVariant = localStorage.getItem(
            `${SplitTest.OngoingFunnelTracking}_VARIANT`
          );
          if (ongoingFunnelTrackingVariant) {
            await trackFunnelEventIfHaveNotAlready(
              SplitTest.OngoingFunnelTracking,
              FunnelFlags.ReportedPurchase,
              ongoingFunnelTrackingVariant,
              entryMethod,
              {
                orderId: orderDetails.order.id,
              }
            );
          }

          if (ENTER_FLOW_SPLIT_TEST_TRACKING) {
            let splitTestVariant;
            if (typeof ENTER_FLOW_SPLIT_TEST_TRACKING === 'object') {
              let test = null;
              const splitTest = ENTER_FLOW_SPLIT_TEST_TRACKING;
              if (localStorage.getItem(`${test}_VARIANT`)) {
                test = splitTest;
                break; // stop the loop
              }
              splitTestVariant = localStorage.getItem(`${test}_VARIANT`);
            } else {
              splitTestVariant = localStorage.getItem(
                `${ENTER_FLOW_SPLIT_TEST_TRACKING}_VARIANT`
              );
            }
            if (splitTestVariant) {
              await trackFunnelEventIfHaveNotAlready(
                ENTER_FLOW_SPLIT_TEST_TRACKING,
                FunnelFlags.ReportedPurchase,
                splitTestVariant,
                entryMethod,
                {
                  orderId: orderDetails.order.id,
                }
              );
            }
          }
        }

        await updateCurrentUser();
        return await navigateToConfirmation(
          Router,
          orderDetails.order.id,
          true,
          entryMethod
        );
      }
      default:
        throw new Error(`Don't know what to do for orderType: ${orderType}`);
    }
  };

  useEffect(() => {
    (async () => {
      if (!orderType) return;
      switch (orderType) {
        case OrderType.TICKET_ORDER:
        case OrderType.GIFT_CARD_ORDER:
        case OrderType.TOKEN_ORDER:
          await checkRegularOrderStatusAndAdvanceToConfirmation();
          break;

        case OrderType.UPDATE_PAYMENT_METHOD:
          await handlePaymentFromChangePaymentMethodPage();
          break;

        default:
          throw new Error(`Don't know what to do for order type ${orderType}`);
      }

      setLoading(false);
    })();

    return () => {
      if (orderCompletionTimerRef.current) {
        clearTimeout(orderCompletionTimerRef.current);
      }
    };
  }, [orderType]);

  // Otherwise, if paymentSessionData exists then the payment was unsuccessful so show the payment form again.
  return (
    <MainLayout>
      <div
        className="paddedMaxWidthContainer"
        id="paymentSection"
        style={{ margin: '4em auto', width: '100%' }}
      >
        {error && (
          <>
            <Message
              id="errorMessage"
              negative
              style={{ width: '100%', maxWidth: '500px ' }}
            >
              {error}
            </Message>
          </>
        )}

        {loading ||
        !sessionId ||
        !paymentSessionData ||
        !adyenPaymentSessionId ? (
          <PlainLoadingSpinner style={{ margin: '5em auto' }} />
        ) : (
          <>
            <AdyenDropInCheckout
              queryParamsObject={{
                source: sourceQueryParam,
                ...(!!subscriptionId && { subscriptionId }),
              }}
              paymentSession={{
                id: sessionId,
                sessionData: paymentSessionData,
                adyenPaymentSessionId,
              }}
              initialRedirectResult={redirectResult}
              onLoadingStateChange={(loading: boolean) => {
                setLoading(loading);
                scrollToSection('#paymentSection');
              }}
              completionHandler={async () => {
                displayHeaderBanner(null);
                await trackEecCheckoutOption('Card');
              }}
              displayError={async (error: string | null) => {
                setError(error);
                if (!error) return;

                const jwt = getCookie('jwt') || getCookie('guest_jwt');
                // Populate session data so we can show the payment form again
                const sessionData = await getAdyenPaymentSessionData(
                  jwt,
                  adyenPaymentSessionId
                );
                setPaymentSessionData(sessionData.sessionData);

                // Scroll to error
                await sleep(200); // Needed, otherwise form doesn't display (after cancelled payment), and to prevent `APP-X` sentry issue.
                scrollToSection('#errorMessage');
              }}
              style={{
                margin: '2em auto',
                width: '100%',
                maxWidth: 500,
              }}
            />
          </>
        )}

        {error && (
          <>
            <Message
              id="errorMessage"
              negative
              style={{ width: '100%', maxWidth: '500px ' }}
            >
              {error}
            </Message>
          </>
        )}
      </div>
    </MainLayout>
  );
});

export default Validate3DSPayment;
